import {useTranslation} from 'hooks/translations/useCustomTranslation';
import useCustomController from 'hooks/useCustomController';
import {ChangeEvent, useEffect, useRef, useState} from 'react';
import {useFormContext} from 'react-hook-form';

import {Box, Stack, StandardTextFieldProps} from '@mui/material';

import TextField from '../TextField';
import Typography from '../Typography';
import styles from './FormInputNativeDate.module.scss';

interface FormInputNativeDateProps extends Omit<StandardTextFieldProps, 'variant'> {
  name: string;
  label?: React.ReactNode;
  variant?: 'standard' | 'filled' | 'outlined';
  subText?: string;
  sx?: StandardTextFieldProps['sx'];
  validateOnBlur?: boolean;
}

export const FormInputFragmentDate = ({
  name,
  label,
  variant,
  subText,
  disabled = false,
  sx,
  validateOnBlur,
}: FormInputNativeDateProps) => {
  const {t} = useTranslation('forms');
  const defaultValue = useRef(null);
  const monthRef = useRef<HTMLInputElement>();
  const yearRef = useRef<HTMLInputElement>();
  const [wasValidated, setWasValidated] = useState(false);

  const {control, trigger} = useFormContext();

  const {
    fieldState: {error},
    field: {value: storedValue, onChange},
  } = useCustomController({
    control,
    name,
    defaultValue: '',
  });

  const [day, setDay] = useState('');
  const [month, setMonth] = useState('');
  const [year, setYear] = useState('');

  const dayInputHandler = (e: ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (value.length <= 2) {
      setDay(value);
    }

    if (value.length == 2) {
      monthRef.current?.focus();
    }
  };

  const monthInputHandler = (e: ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (value.length <= 2) {
      setMonth(value);
    }

    if (value.length == 2) {
      yearRef.current?.focus();
    }
  };

  const yearInputHandler = (e: ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (value.length <= 4) {
      setYear(value);
    }
  };

  const validateDate = () => {
    const y = parseInt(year, 10);
    const m = parseInt(month, 10);
    const d = parseInt(day, 10);
    const date = new Date(y, m - 1, d);

    let valueToSet = '';

    if (isNaN(y) || isNaN(m) || isNaN(d)) {
      valueToSet = '';
    } else if (date.getMonth() !== m - 1 || date.getDate() !== d) {
      valueToSet = `0000-00-00`;
    } else {
      valueToSet = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    }

    onChange({
      target: {
        name,
        value: valueToSet,
      },
    });

    // Live revalidation after first manual trigger
    if (wasValidated) {
      trigger(name);
    }
  };

  useEffect(() => {
    validateDate();
  }, [day, month, year]);

  useEffect(() => {
    if (defaultValue.current === '' || storedValue === '') return;
    if (!defaultValue.current || defaultValue.current === storedValue) {
      defaultValue.current = storedValue;
      const dateParts = new Date(storedValue);
      if (!isNaN(dateParts.getTime())) {
        setYear(dateParts.getFullYear().toString() || '');
        setMonth((dateParts.getMonth() + 1).toString().padStart(2, '0') || '');
        setDay(dateParts.getDate().toString().padStart(2, '0') || '');
      }
    }
  }, [storedValue]);

  const handleInput = event => {
    const target = event.nativeEvent.target;
    function iosClearDefault() {
      target.defaultValue = '';
    }
    window.setTimeout(iosClearDefault, 0);
  };

  return (
    <Box
      onBlurCapture={e => {
        const currentTarget = e.currentTarget;
        const relatedTarget = e.relatedTarget as HTMLElement | null;

        if (!currentTarget.contains(relatedTarget)) {
          // Entire group lost focus
          if (validateOnBlur) {
            setWasValidated(true);
            trigger(name);
          }
        }
      }}
    >
      <Typography
        display="block"
        mb={1}
        sx={{
          color: theme => theme.palette.text.secondary,
          fontSize: theme => theme.typography.pxToRem(9),
          fontWeight: 400,
        }}
      >
        {label}
      </Typography>
      <Stack direction="row" spacing={2}>
        <TextField
          type="number"
          autoComplete="bday-day"
          variant={variant}
          error={!!error}
          placeholder={t('dateInputPlaceholderDay')}
          value={day}
          onChange={dayInputHandler}
          onBlur={e => {
            if (e.target.value.length === 1) setDay(day.padStart(2, '0'));
            if (validateOnBlur) validateDate();
          }}
          onInput={handleInput}
          name={`${name}.day`}
          className={styles.dateField}
          inputProps={{
            max: 31,
            min: 1,
            'data-empty': day === '' ? 'noValue' : 'hasValue',
          }}
          sx={{
            ...sx,
            '& ::placeholder': {
              fontWeight: '500',
              opacity: 1,
            },
          }}
          disabled={disabled}
        />

        <TextField
          ref={monthRef}
          type="number"
          autoComplete="bday-month"
          variant={variant}
          error={!!error}
          placeholder={t('dateInputPlaceholderMonth')}
          value={month}
          onChange={monthInputHandler}
          onBlur={e => {
            if (e.target.value.length === 1) setMonth(month.padStart(2, '0'));
            if (validateOnBlur) validateDate();
          }}
          onInput={handleInput}
          name={`${name}.month`}
          className={styles.dateField}
          inputProps={{
            max: 12,
            min: 1,
            'data-empty': month === '' ? 'noValue' : 'hasValue',
          }}
          sx={{
            ...sx,
            '& ::placeholder': {
              fontWeight: '500',
              opacity: 1,
            },
          }}
          disabled={disabled}
        />

        <TextField
          ref={yearRef}
          type="number"
          autoComplete="bday-year"
          variant={variant}
          error={!!error}
          placeholder={t('dateInputPlaceholderYear')}
          value={year}
          onChange={yearInputHandler}
          onBlur={() => validateOnBlur && validateDate()}
          onInput={handleInput}
          name={`${name}.year`}
          className={styles.dateField}
          inputProps={{
            max: 9999,
            min: 1900,
            'data-empty': year === '' ? 'noValue' : 'hasValue',
          }}
          sx={{
            ...sx,
            '& ::placeholder': {
              fontWeight: '500',
              opacity: 1,
            },
          }}
          disabled={disabled}
        />
      </Stack>
      <Typography variant="formLabeling" color="error" mt={1}>
        {error?.message}
      </Typography>
    </Box>
  );
};
